import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { BgColorProvider, useBgColorCtx } from '~theme/BgColorProvider'
import { useFgColorValue } from '~theme/fgColor'
import { TActiveColorKey } from '~theme/typings'
import { getActiveColorFullPath } from '~theme/utils'
import { Icon } from '~ui/components/icons/Icon'
import { cn } from '~ui/libs/classnames'
import { getRadiusValue, TRadius, TSingleRadius } from '~ui/styleGuide/radiuses'
import { shadows, TInnerShadow, TOuterShadow } from '~ui/styleGuide/shadows'
import {
  getBottomSpacingValue,
  getLeftSpacingValue,
  getRightSpacingValue,
  getSpacingValue,
  getTopSpacingValue,
  TDoubleSpace,
  TSpace,
} from '~ui/styleGuide/spaces'
import { durations } from '~ui/styleGuide/styles'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'
import { TChakraPositionProps } from '~ui/typings/chakra'
import { useHoverState } from '~ui/utils/useHoverState'

import { TButtonVariantProps } from './buildButtonVariantProps'

type TChakraProps = TChakraPositionProps &
  Pick<
    ChakraButtonProps,
    | 'type'
    | 'width'
    | 'onClick'
    | 'marginLeft'
    | 'marginRight'
    | 'minWidth'
    | 'maxWidth'
    | 'height'
    // TODO tooltip
    | 'title'
    | 'marginTop'
    | 'marginBottom'
    | 'justifyContent'
  >

type TIconProps = {
  icon: PropsOf<typeof Icon>['icon']
  size?: PropsOf<typeof Icon>['iconSize']
  color?: PropsOf<typeof Icon>['color']
  contrast?: PropsOf<typeof Icon>['contrast']
}

type TButtonSize = 'small' | 'medium' | 'large'

export const SIZE_PADDINGS: Record<TButtonSize, TDoubleSpace> = {
  small: { vertical: `extraSmall`, horizontal: `smallMedium` },
  medium: { vertical: `small`, horizontal: `medium` },
  large: { vertical: `smallMedium`, horizontal: `large` },
}

type TIconSizeProps = {
  iconSize: TIconProps['size']
  iconSpacing: TSpace
}

const ICON_SIZES: Record<TButtonSize, TIconSizeProps> = {
  small: { iconSize: `0.875rem`, iconSpacing: `extraSmall` },
  medium: { iconSize: `1rem`, iconSpacing: `extraSmall` },
  large: { iconSize: `1.25rem`, iconSpacing: `small` },
}

type TStyledProps = {
  bgColor: string
  fgColor: string
  borderColor: string
  animationDuration: number
}

type TProps = RequiredChildren &
  TestingProps &
  TChakraProps &
  TButtonVariantProps & {
    /**
     * key of radius,
     * or an object with separate key for each corner
     */
    radius?: TRadius
    /**
     * sets predefined horizontal & vertical padding values to size the button
     *
     * if you need to use custom values use the `padding` prop instead
     */
    size?: TButtonSize
    /**
     * space inside the button,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     *
     * gets priority over the `size` prop
     */
    padding?: TSpace
    /**
     * props for icon inside the button on the left side of the children
     */
    leftIcon?: TIconProps
    /**
     * props for icon inside the button on the right side of the children
     */
    rightIcon?: TIconProps
    /**
     * key of outer box shadows
     */
    shadow?: TOuterShadow
    /**
     * key of outer box shadows
     */
    shadowInset?: TInnerShadow
    /**
     * css class that will be passed to the styled/html button
     */
    className?: string
    /**
     * state of the button, replaces `isDisabled` and `isLoading` props,
     * blocking state is visually similar to disabled but with `onBlockedClick` cb
     */
    state?: 'loading' | 'blocked' | 'enabled' | 'disabled'
    /**
     * onClick used when the button is in `blocked` state
     */
    onBlockedClick?: TChakraProps['onClick']
    /**
     * if true, the button transition will take 0ms (will be instant)
     */
    turnOffTransition?: boolean
  }

// props passed just between the wrapper component & the implementation one
type TInnerProps = {
  isHovered: boolean
  onPointerEnter?: PropsOf<typeof ChakraButton>['onPointerEnter']
  onPointerLeave?: PropsOf<typeof ChakraButton>['onPointerLeave']
}

// get rid of the React error when passing unknown prop to the HTML element
const propForwardOptions = {
  shouldForwardProp: (propName: string) =>
    propName !== `bgColor` &&
    propName !== `fgColor` &&
    propName !== `borderColor` &&
    propName !== `animationDuration`,
}

const StyledButton = styled(ChakraButton, propForwardOptions)<TStyledProps>`
  cursor: pointer;

  transition: none;
  transition:
    background-color ${({ animationDuration }) => animationDuration}ms,
    border-color ${({ animationDuration }) => animationDuration}ms,
    color ${({ animationDuration }) => animationDuration}ms;

  background-color: ${({ bgColor }) => bgColor};
  border-color: ${({ borderColor }) => borderColor};
  color: ${({ fgColor }) => fgColor};

  &:disabled {
    cursor: not-allowed;
  }

  /* class taken from HTML */
  .chakra-button__icon {
    /* needed for vertical alignment of the icons */
    display: flex;
  }
`

const DEFAULT_VARIANT: TProps['variant'] = 'solid'
export const BUTTON_BORDER_WIDTH = `2px`
export const BUTTON_DEFAULT_RADIUS: TSingleRadius = `small`

const COMPENSATE_ICON_WIDTH = `0.25rem`
const DEFAULT_ICON_SIZE = `1.2rem`
const DEFAULT_ICON_SPACING = `0.5rem`

/**
 * actual implementation
 * used inside a BgProviderWrapper bellow
 */
const ButtonComponent = React.forwardRef<HTMLButtonElement, TProps & TInnerProps>(
  (
    {
      bgColor: bgColorProp,
      children,
      leftIcon,
      rightIcon,
      className,
      shadow = `none`,
      shadowInset,
      radius = BUTTON_DEFAULT_RADIUS,
      variant = DEFAULT_VARIANT,
      size,
      padding,
      borderContrast = `normal`,
      dataTestId,
      isHovered,
      onPointerEnter,
      onPointerLeave,
      state = 'enabled',
      onClick,
      onBlockedClick,
      turnOffTransition = false,
      ...chakraProps
    },
    ref,
  ) => {
    const { bgColor, bgColorValue } = useBgColorCtx()
    const fgColorValue = useFgColorValue({ contrast: `normal` })
    const outlineColorValue = useFgColorValue({ contrast: borderContrast })

    const isLoading = state === 'loading'
    const isBlocked = state === 'blocked'
    const isDisabled = state === 'disabled'

    const colors = React.useMemo<OmitSafe<TStyledProps, 'animationDuration'>>(() => {
      const hasBgColorSet = Boolean(bgColorProp)
      const mainColor = hasBgColorSet ? bgColorValue : `transparent`

      switch (variant) {
        case 'ghost':
          return {
            bgColor: isHovered ? mainColor : `transparent`,
            fgColor: fgColorValue,
            borderColor: `transparent`,
          }

        case 'outline':
          return {
            bgColor: isHovered ? mainColor : `transparent`,
            fgColor: fgColorValue,
            borderColor: isHovered ? mainColor : outlineColorValue,
          }

        case 'solid':
        default:
          return {
            bgColor: mainColor,
            fgColor: fgColorValue,
            borderColor: mainColor,
          }
      }
    }, [bgColorProp, bgColorValue, fgColorValue, isHovered, outlineColorValue, variant])

    const shadowValue = React.useMemo(() => {
      if (variant === 'ghost') {
        return undefined
      }

      const outerShadowValue = shadow && shadows.outer[shadow]
      const innerShadowValue = shadowInset && shadows.inner[shadowInset]

      return innerShadowValue ?? outerShadowValue
    }, [shadow, shadowInset, variant])

    const paddingKey = React.useMemo(() => {
      if (padding) return padding
      if (size) return SIZE_PADDINGS[size]

      return `medium`
    }, [padding, size])

    const hasLeftIcon = Boolean(leftIcon)
    const hasRightIcon = Boolean(rightIcon)
    const hasBorder = shadowInset === undefined

    const borderWidth = hasBorder ? BUTTON_BORDER_WIDTH : `0px` // to make transitions look better
    const paddingProps = getPaddingProps({ hasBorder, hasLeftIcon, hasRightIcon })(paddingKey)
    const borderRadius = getRadiusValue(radius)
    const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColorProp ? bgColor : null }

    const getIcon = getIconElement({ buttonSize: size, isLoading })

    return (
      <StyledButton
        {...colors}
        {...chakraProps}
        {...paddingProps}
        {...themeDesignProps}
        ref={ref}
        isLoading={isLoading}
        boxShadow={shadowValue}
        isDisabled={isDisabled}
        data-testid={dataTestId}
        borderRadius={borderRadius}
        className={cn(variant, className)}
        leftIcon={getIcon(leftIcon)}
        rightIcon={getIcon(rightIcon)}
        iconSpacing={getIconSpacingValue(size)}
        borderWidth={borderWidth}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave} // onMouseLeave does not fire on disabled elements: https://github.com/facebook/react/issues/18753
        borderStyle={`solid`}
        onClick={isBlocked ? onBlockedClick : onClick}
        whiteSpace={`pre-line`}
        animationDuration={turnOffTransition ? 0 : durations.short}
      >
        {children}
      </StyledButton>
    )
  },
)

/**
 * wrapped Chakra Button used as a main button element
 */
export const Button = React.forwardRef<HTMLButtonElement, TProps>((props, ref) => {
  // this is just a BgColorProvider wrapper, the actual component is above
  const { isHovered, setHoverStart, setHoverEnd } = useHoverState()

  const isDisabledState = props.state === 'disabled' || props.state === 'blocked'

  const buttonBgColor = getButtonBgColor({
    isHovered,
    isDisabledState,
    bgColor: props.bgColor,
    variant: props.variant ?? DEFAULT_VARIANT,
  })

  return (
    <BgColorProvider bgColor={buttonBgColor} isDisabledState={isDisabledState}>
      <ButtonComponent
        ref={ref}
        {...props}
        isHovered={isHovered}
        onPointerLeave={setHoverEnd}
        onPointerEnter={setHoverStart}
      />
    </BgColorProvider>
  )
})

// * HELPERS

type TGetButtonBgColorOptions = {
  isHovered: boolean
  isDisabledState: boolean
  bgColor?: TActiveColorKey
  variant: TProps['variant']
}

const getButtonBgColor = ({
  bgColor,
  variant,
  isHovered,
  isDisabledState,
}: TGetButtonBgColorOptions): PropsOf<typeof BgColorProvider>['bgColor'] => {
  const showDisabledColor = variant === `solid` || isHovered

  // the order of these conditions actually matter
  if (!bgColor) return null
  if (isDisabledState && showDisabledColor)
    return getActiveColorFullPath({ bgColor, isDisabled: true })
  if (isHovered) return getActiveColorFullPath({ bgColor, isHovered: true })
  if (variant === `ghost`) return `transparent`
  if (variant === `outline`) return `transparent`

  return `activeColors.${bgColor}.normal`
}

// decides how big the icon should be, based on the button size
const getDefaultIconSize = (size: TProps['size']) => {
  if (!size) return DEFAULT_ICON_SIZE

  return ICON_SIZES[size].iconSize
}

type TGetIconElementOptions = {
  isLoading?: boolean
  buttonSize?: TProps['size']
}

const getIconElement =
  ({ isLoading, buttonSize }: TGetIconElementOptions) =>
  (iconProps?: TIconProps) => {
    if (!iconProps) return undefined
    if (isLoading) return undefined

    const defaultIconSize = getDefaultIconSize(buttonSize)

    return (
      <Icon
        icon={iconProps.icon}
        iconSize={iconProps.size ?? defaultIconSize}
        color={iconProps.color ?? undefined}
        contrast={iconProps.contrast}
      />
    )
  }

// decides how much space should be between the icon and the label
const getIconSpacingValue = (size: TProps['size']) => {
  if (!size) return DEFAULT_ICON_SPACING

  const iconSpacing = ICON_SIZES[size].iconSpacing

  return getSpacingValue(iconSpacing)
}

type TPaddingOptions = {
  hasBorder: boolean
  hasLeftIcon: boolean
  hasRightIcon: boolean
}

const getPaddingProps =
  ({ hasBorder, hasLeftIcon, hasRightIcon }: TPaddingOptions) =>
  (padding: TSpace) => {
    if (!hasBorder && !hasLeftIcon && !hasRightIcon) {
      return { padding: getSpacingValue(padding) }
    }

    // compensate border width by decreased padding values
    const topPaddingValue = getTopSpacingValue(padding)
    const bottomPaddingValue = getBottomSpacingValue(padding)
    const rightPaddingValue = getRightSpacingValue(padding)
    const leftPaddingValue = getLeftSpacingValue(padding)

    return {
      paddingTop: compensateWidth({ paddingValue: topPaddingValue }),
      paddingBottom: compensateWidth({ paddingValue: bottomPaddingValue }),
      paddingRight: compensateWidth({ paddingValue: rightPaddingValue, hasIcon: hasRightIcon }),
      paddingLeft: compensateWidth({ paddingValue: leftPaddingValue, hasIcon: hasLeftIcon }),
    }
  }

type TCompensateWidthIconOptions = {
  paddingValue: string
  hasIcon?: boolean
}

// compensate border width & icon by decreased padding values
const compensateWidth = ({ paddingValue, hasIcon }: TCompensateWidthIconOptions) => {
  const compensateIconWidth = hasIcon ? ` - ${COMPENSATE_ICON_WIDTH}` : ``
  const compensateBorderWidth = ` - ${BUTTON_BORDER_WIDTH}`

  return `calc(${paddingValue}${compensateBorderWidth}${compensateIconWidth})`
}
